import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../utils/useOnboarding';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { Rocket, CheckCircle, ArrowRight, MessageCircle, ShieldCheck, X, RefreshCw } from 'lucide-react';
import { useApi } from '../../utils/useApi';

// ------------------------------------
// INTERFACES
// ------------------------------------

interface GymFormData {
  gymName: string;
  gymCode: string;
  gymGstin?: string;
  gymAddressLine: string;
  gymCity: string;
  gymPincode: string;
}

interface BranchFormData {
  branchName: string;
  branchAddress: string;
  branchCity: string;
  branchPincode: string;
}

interface CompleteOnboardingData extends BranchFormData, GymFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  gymRegisteredAddress: string;
  gymDisplayName: string;
  branchDisplayName: string;
}

// ------------------------------------
// SUCCESS ANIMATION COMPONENT
// ------------------------------------

const SuccessRocket: React.FC = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-zinc-950 text-white p-8">
    <div className="relative flex flex-col items-center">
      <Rocket className="w-20 h-20 mb-6 text-[#AAD816] animate-bounce-slow" style={{ transform: 'rotate(45deg)' }} />
      <div className="absolute top-20 w-4 h-4 bg-[#AAD816] rounded-full blur-sm animate-ping" />
      <div className="absolute top-24 w-8 h-8 bg-[#AAD816]/30 rounded-full blur-md opacity-70 animate-pulse" />
      <h1 className="text-4xl font-black mt-10 tracking-tight">Setup Complete!</h1>
      <p className="mt-4 text-xl font-medium text-zinc-400">Launching you to the dashboard...</p>
    </div>
  </div>
);

// ------------------------------------
// WHATSAPP CONFIRM MODAL
// ------------------------------------

interface WhatsAppConfirmModalProps {
  phoneNumber: string; // full 10-digit number
  onConfirm: () => void;
  onCancel: () => void;
  sending: boolean;
}

const WhatsAppConfirmModal: React.FC<WhatsAppConfirmModalProps> = ({ phoneNumber, onConfirm, onCancel, sending }) => {
  const formatted = `+91 ${phoneNumber.slice(0, 5)} ${phoneNumber.slice(5)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm"
        onClick={!sending ? onCancel : undefined}
      />

      {/* Modal card */}
      <div className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-8 animate-in fade-in zoom-in-95 duration-200">
        {/* Close */}
        {!sending && (
          <button
            onClick={onCancel}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-100 hover:bg-zinc-200 transition-colors"
          >
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        )}

        {/* WhatsApp icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-[1.25rem] bg-[#25D366] flex items-center justify-center shadow-lg shadow-[#25D366]/30">
            <MessageCircle className="w-9 h-9 text-white fill-white" />
          </div>
        </div>

        <h2 className="text-xl font-black text-zinc-900 text-center tracking-tight">
          Verify via WhatsApp
        </h2>
        <p className="mt-2 text-sm text-zinc-500 text-center leading-relaxed">
          We'll send a 6-digit OTP to your WhatsApp. Please make sure this number is active on WhatsApp.
        </p>

        {/* Phone number highlight */}
        <div className="mt-5 p-4 bg-zinc-950 rounded-2xl text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-400 mb-1">Sending OTP to</p>
          <p className="text-lg font-black text-[#AAD816] tracking-widest">{formatted}</p>
        </div>

        <p className="mt-4 text-xs text-center text-zinc-400 font-medium">
          Do you have WhatsApp on this number?
        </p>

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancel}
            disabled={sending}
            className="flex-1 py-3 rounded-xl border-2 border-zinc-200 text-xs font-black uppercase tracking-widest text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 transition-all disabled:opacity-40"
          >
            No, Change
          </button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className="flex-1 py-3 rounded-xl bg-zinc-950 text-xs font-black uppercase tracking-widest text-[#AAD816] hover:bg-[#AAD816] hover:text-zinc-950 transition-all shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-[#AAD816]/30 border-t-[#AAD816] rounded-full animate-spin" />
                Sending…
              </>
            ) : (
              'Yes, Send OTP'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------
// OTP INPUT COMPONENT
// ------------------------------------

interface OtpInputProps {
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({ value, onChange, disabled }) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (idx: number, char: string) => {
    const digits = char.replace(/\D/g, '');
    if (!digits) return;
    const newVal = (value + digits).slice(0, 6);
    const arr = newVal.padEnd(6, ' ').split('');
    onChange(arr.join('').trimEnd());
    // Move focus forward
    if (digits && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = value.split('');
      if (arr[idx]) {
        arr[idx] = '';
      } else if (idx > 0) {
        arr[idx - 1] = '';
        inputsRef.current[idx - 1]?.focus();
      }
      onChange(arr.join(''));
    } else if (e.key === 'ArrowLeft') {
      inputsRef.current[Math.max(0, idx - 1)]?.focus();
    } else if (e.key === 'ArrowRight') {
      inputsRef.current[Math.min(5, idx + 1)]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: 6 }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => { inputsRef.current[idx] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[idx] || ''}
          disabled={disabled}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={`w-11 h-14 text-center text-xl font-black rounded-xl border-2 outline-none transition-all duration-150
            ${value[idx]
              ? 'border-[#AAD816] bg-[#AAD816]/5 text-zinc-900'
              : 'border-zinc-200 bg-zinc-50 text-zinc-400'
            }
            focus:border-zinc-950 focus:bg-white
            disabled:opacity-50 disabled:cursor-not-allowed`}
        />
      ))}
    </div>
  );
};

// ------------------------------------
// MAIN COMPONENT
// ------------------------------------

type OtpState = 'idle' | 'confirming' | 'sending' | 'waiting_input' | 'verifying' | 'verified';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const { loading: onboardingLoading, submitting, completeOnboarding, validateGstin: apiValidateGstin } = useOnboarding();
  const { fetchWithAuth } = useApi();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  const [validatingGstin, setValidatingGstin] = useState(false);

  // OTP flow state
  const [otpState, setOtpState] = useState<OtpState>('idle');
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining

  // Phone number editable in Step 2 (pre-filled from profile, but user may change it)
  const [verifyPhone, setVerifyPhone] = useState('');

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });

  const [gymData, setGymData] = useState<GymFormData>({
    gymName: '',
    gymCode: '',
    gymGstin: '',
    gymAddressLine: '',
    gymCity: '',
    gymPincode: ''
  });

  const [branchData, setBranchData] = useState<BranchFormData>({
    branchName: '',
    branchAddress: '',
    branchCity: '',
    branchPincode: ''
  });

  const firstName = profileData.firstName;

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Keep verifyPhone in sync with profileData on initial load (don't overwrite if user already edited)
  useEffect(() => {
    if (profileData.phoneNumber && !verifyPhone) {
      setVerifyPhone(profileData.phoneNumber);
    }
  }, [profileData.phoneNumber]);

  // When the phone number is changed, reset OTP state so user must re-verify
  const handleVerifyPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setVerifyPhone(raw);
    if (otpState !== 'idle') {
      setOtpState('idle');
      setOtpValue('');
      setOtpError(null);
      setResendCooldown(0);
    }
  };

  useEffect(() => {
    if (user?.user_metadata) {
      const metadata = user.user_metadata;
      setProfileData({
        firstName: metadata.first_name || '',
        lastName: metadata.last_name || '',
        email: user.email || '',
        phoneNumber: metadata.phone_number || ''
      });

      if (!gymData.gymName && metadata.first_name) {
        setGymData(prev => ({ ...prev, gymName: `${metadata.first_name}'s Fitness` }));
      }
    }

    if (user && user.user_metadata?.onboarding_complete) {
      setTimeout(() => navigate('/dashboard'), 3000);
    }
  }, [user, navigate]);

  const steps = [
    { number: 1, title: 'Gym Details', description: 'Business info' },
    { number: 2, title: 'First Branch', description: 'Location & Verify' },
  ];

  const [isGymCodeManuallyEdited, setIsGymCodeManuallyEdited] = useState(false);

  const getFYText = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    if (month >= 4) {
      const nextYear = year + 1;
      return `${year.toString().slice(-2)}${nextYear.toString().slice(-2)}`;
    } else {
      const prevYear = year - 1;
      return `${prevYear.toString().slice(-2)}${year.toString().slice(-2)}`;
    }
  };

  const calculateSampleIds = (code: string) => {
    const cleanCode = code ? code.toUpperCase().replace(/[^A-Z0-9]/g, '') : 'YOURGYM';
    const fy = getFYText();
    return {
      invoice: `${cleanCode}INV${fy}0001`,
      employee: `${cleanCode}EMP0001`,
      payslip: `${cleanCode}PAY${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}0001`
    };
  };

  const validateGstinFormat = (gstin: string) => {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstinRegex.test(gstin);
  };

  const handleGymNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setGymData(prev => {
      const updates: any = { gymName: name };
      if (!isGymCodeManuallyEdited) {
        updates.gymCode = name.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
      }
      return { ...prev, ...updates };
    });
  };

  const handleGymCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setGymData(prev => ({ ...prev, gymCode: value }));
    setIsGymCodeManuallyEdited(true);
  };

  const validateStep = (step: number): boolean => {
    if (step === 1) {
      return (
        !!gymData.gymName.trim() &&
        !!gymData.gymCode.trim() &&
        gymData.gymCode.length >= 3 &&
        !!gymData.gymAddressLine.trim() &&
        !!gymData.gymCity.trim() &&
        // GSTIN is optional — but if provided it must be a valid format
        (!gymData.gymGstin || validateGstinFormat(gymData.gymGstin))
      );
    }
    if (step === 2) {
      return !!branchData.branchName.trim() && otpState === 'verified';
    }
    return false;
  };

  const handleNext = async () => {
    if (!validateStep(1)) {
      if (!gymData.gymName.trim()) toast.error('Please enter Gym Name');
      else if (!gymData.gymAddressLine.trim() || !gymData.gymCity.trim()) toast.error('Please enter Registered Office Address and City');
      else if (!gymData.gymCode || gymData.gymCode.length < 3) toast.error('Please enter a valid Gym Code (min 3 chars)');
      else if (gymData.gymGstin && !validateGstinFormat(gymData.gymGstin)) toast.error('Please enter a valid GSTIN');
      else toast.error('Please fill in all required fields');
      return;
    }

    // GSTIN is optional — only run the uniqueness check when one was entered
    if (gymData.gymGstin) {
      setValidatingGstin(true);
      try {
        const response = await apiValidateGstin(gymData.gymGstin);
        if (!response.available) {
          toast.error(response.message || 'GSTIN is already in use');
          return;
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to validate GSTIN. Please try again.');
        return;
      } finally {
        setValidatingGstin(false);
      }
    }

    setCurrentStep(2);
  };

  const handlePrevious = () => {
    setCurrentStep(1);
    // Reset OTP state when going back
    setOtpState('idle');
    setOtpValue('');
    setOtpError(null);
  };

  const handleBranchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBranchData({ ...branchData, [e.target.name]: e.target.value });
  };

  // -- OTP Handlers --

  const handleVerifyPhoneClick = () => {
    if (!verifyPhone || verifyPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number.');
      return;
    }
    setOtpState('confirming');
  };

  const handleConfirmSendOtp = async () => {
    setOtpState('sending');
    setOtpError(null);
    try {
      const res = await fetchWithAuth('/api/onboarding/send-otp', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber: verifyPhone })
      });
      if (res.success) {
        setResendCooldown(60); // 60 second cooldown before resend
        setOtpState('waiting_input');
        toast.success('OTP sent! Check your WhatsApp.');
      } else {
        toast.error(res.error || 'Failed to send OTP.');
        setOtpState('idle');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to send OTP.');
      setOtpState('idle');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setOtpError(null);
    setOtpValue('');
    setOtpState('sending');
    try {
      const res = await fetchWithAuth('/api/onboarding/send-otp', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber: verifyPhone })
      });
      if (res.success) {
        setResendCooldown(60);
        setOtpState('waiting_input');
        toast.success('New OTP sent!');
      } else {
        toast.error(res.error || 'Failed to resend OTP.');
        setOtpState('waiting_input');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to resend OTP.');
      setOtpState('waiting_input');
    }
  };

  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) {
      setOtpError('Please enter all 6 digits.');
      return;
    }
    setOtpState('verifying');
    setOtpError(null);
    try {
      const res = await fetchWithAuth('/api/onboarding/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber: verifyPhone, otp: otpValue })
      });
      if (res.success && res.data?.phoneVerified) {
        setOtpState('verified');
        toast.success('Phone verified! 🎉');
      } else {
        setOtpError(res.error || 'Invalid OTP. Please try again.');
        setOtpState('waiting_input');
      }
    } catch (err: any) {
      setOtpError(err.message || 'Verification failed. Please try again.');
      setOtpState('waiting_input');
    }
  };

  const handleSubmit = async () => {
    if (otpState !== 'verified') {
      toast.error('Please verify your phone number first.');
      return;
    }

    const combinedAddress = [gymData.gymAddressLine, gymData.gymCity, gymData.gymPincode]
      .filter(Boolean)
      .join(', ');

    const completeData: CompleteOnboardingData = {
      ...profileData,
      phoneNumber: verifyPhone, // use the verified phone, overriding the profile default
      ...gymData,
      ...branchData,
      branchAddress: combinedAddress,
      branchCity: gymData.gymCity,
      branchPincode: gymData.gymPincode,
      gymRegisteredAddress: combinedAddress,
      gymDisplayName: gymData.gymName,
      branchDisplayName: branchData.branchName,
    };

    try {
      await completeOnboarding(completeData);
      setIsSuccess(true);

      if (typeof refreshUser === 'function') {
        await refreshUser();
      }

      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to complete setup';
      if (errorMessage.includes('database')) {
        toast.error('A database error occurred. Please try again.');
      } else if (errorMessage.includes('authenticated')) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(errorMessage);
      }
      setIsSuccess(false);
    }
  };

  if (authLoading || onboardingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-zinc-600 font-bold uppercase tracking-widest text-xs">Syncing Data...</p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return <SuccessRocket />;
  }

  const phoneFormatted = verifyPhone && verifyPhone.length === 10
    ? `+91 ${verifyPhone.slice(0, 5)} ${verifyPhone.slice(5)}`
    : verifyPhone ? `+91 ${verifyPhone}` : '';

  return (
    <div className="min-h-screen bg-zinc-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      {/* WhatsApp Confirm Modal */}
      {otpState === 'confirming' && (
        <WhatsAppConfirmModal
          phoneNumber={verifyPhone}
          onConfirm={handleConfirmSendOtp}
          onCancel={() => setOtpState('idle')}
          sending={false}
        />
      )}
      {otpState === 'sending' && (
        <WhatsAppConfirmModal
          phoneNumber={verifyPhone}
          onConfirm={handleConfirmSendOtp}
          onCancel={() => setOtpState('idle')}
          sending={true}
        />
      )}

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-8">
            <img src="/gympe.svg" alt="Gympe" className="h-10 w-auto" />
          </div>
          <h1 className="text-4xl font-black text-zinc-900 tracking-tight">
            Hey, {firstName || 'there'}!
          </h1>
          <p className="mt-3 text-zinc-500 font-medium text-lg">Let's blueprint your fitness empire.</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center group">
                <div className={`flex items-center justify-center w-12 h-12 rounded-2xl border-2 transition-all duration-300 ${currentStep >= step.number
                  ? 'bg-zinc-950 border-zinc-950 text-[#AAD816] shadow-xl shadow-zinc-200'
                  : 'bg-white border-zinc-200 text-zinc-300'
                  }`}>
                  {currentStep > step.number ? (
                    <CheckCircle className="w-6 h-6 stroke-[3]" />
                  ) : (
                    <span className="text-sm font-black tracking-tighter">{String(step.number).padStart(2, '0')}</span>
                  )}
                </div>
                <div className="ml-4">
                  <p className={`text-xs font-black uppercase tracking-[0.15em] ${currentStep >= step.number ? 'text-zinc-900' : 'text-zinc-300'}`}>
                    {step.title}
                  </p>
                  <p className="text-[10px] font-bold text-zinc-400 mt-1 uppercase tracking-widest">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 min-w-[3rem] h-0.5 mx-6 transition-all duration-500 ${currentStep > step.number ? 'bg-zinc-950' : 'bg-zinc-100'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl border border-zinc-100 p-10">

          {/* ── STEP 1: Gym Details ── */}
          {currentStep === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

              <div className="space-y-2">
                <label htmlFor="gymName" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">
                  Gym Name *
                </label>
                <input
                  id="gymName"
                  name="gymName"
                  type="text"
                  required
                  value={gymData.gymName}
                  onChange={handleGymNameChange}
                  className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium placeholder-zinc-300 outline-none"
                  placeholder="e.g., Alpha Fitness Center"
                />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="gymAddressLine" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">
                    Registered Office Address *
                  </label>
                  <input
                    id="gymAddressLine"
                    name="gymAddressLine"
                    type="text"
                    required
                    value={gymData.gymAddressLine}
                    onChange={(e) => setGymData({ ...gymData, gymAddressLine: e.target.value })}
                    className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium placeholder-zinc-300 outline-none"
                    placeholder="Street, Building, Area"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="gymCity" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">City *</label>
                    <input
                      id="gymCity"
                      name="gymCity"
                      type="text"
                      required
                      value={gymData.gymCity}
                      onChange={(e) => setGymData({ ...gymData, gymCity: e.target.value })}
                      className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium placeholder-zinc-300 outline-none"
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="gymPincode" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">Pincode</label>
                    <input
                      id="gymPincode"
                      name="gymPincode"
                      type="text"
                      value={gymData.gymPincode}
                      onChange={(e) => setGymData({ ...gymData, gymPincode: e.target.value })}
                      className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium placeholder-zinc-300 outline-none"
                      placeholder="6-digit PIN"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="gymCode" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">
                  Gym Code *
                </label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <input
                      id="gymCode"
                      name="gymCode"
                      type="text"
                      maxLength={6}
                      value={gymData.gymCode}
                      onChange={handleGymCodeChange}
                      className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-black uppercase tracking-widest placeholder-zinc-300 outline-none"
                      placeholder="ALPHA"
                    />
                    <p className="mt-2 text-[10px] font-bold text-zinc-400 uppercase tracking-widest px-1">
                      Basis for all your automated IDs (Max 6 chars)
                    </p>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="mt-6 p-6 bg-zinc-950 rounded-2xl border border-zinc-800 shadow-inner">
                  <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em] mb-4">Live ID Preview</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest">Invoices</span>
                      <span className="text-xs font-black text-[#AAD816] bg-[#AAD816]/10 px-2 py-1 rounded">
                        {calculateSampleIds(gymData.gymCode).invoice}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-800 px-1">
                      <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest">Employee IDs</span>
                      <span className="text-xs font-black text-zinc-100 italic">
                        {calculateSampleIds(gymData.gymCode).employee}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="gymGstin" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">
                  GSTIN <span className="text-zinc-400/60">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    id="gymGstin"
                    name="gymGstin"
                    type="text"
                    maxLength={15}
                    value={gymData.gymGstin || ''}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setGymData({ ...gymData, gymGstin: val });
                    }}
                    className={`w-full px-5 py-4 bg-zinc-50 border rounded-2xl focus:ring-2 transition-all duration-200 font-black uppercase tracking-widest outline-none ${gymData.gymGstin && !validateGstinFormat(gymData.gymGstin)
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-zinc-200 focus:ring-[#AAD816]'
                      }`}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>

                {gymData.gymGstin && !validateGstinFormat(gymData.gymGstin) ? (
                  <p className="mt-1 text-[10px] font-bold text-red-500 uppercase ml-1">Invalid GSTIN format. Example: 22AAAAA0000A1Z5</p>
                ) : (
                  <div className="mt-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100/50 flex gap-3">
                    <span className="text-blue-500 text-sm italic font-black">!</span>
                    <p className="text-[10px] font-bold text-blue-600/80 uppercase tracking-wider leading-relaxed">
                      Optional — add it for GST compliant billing and B2B transactions. You can set this up later in Settings.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── STEP 2: Branch + Phone Verification ── */}
          {currentStep === 2 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

              {/* Branch Name */}
              <div className="space-y-2">
                <label htmlFor="branchName" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1">
                  Branch Name *
                </label>
                <input
                  id="branchName"
                  name="branchName"
                  type="text"
                  required
                  value={branchData.branchName}
                  onChange={handleBranchChange}
                  className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none"
                  placeholder="e.g., Downtown Elite"
                />
              </div>

              {/* Gym address preview */}
              <div className="p-6 bg-[#AAD816]/5 border border-[#AAD816]/20 rounded-2xl">
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-3 px-1">Using Gym Address</p>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm border border-zinc-100 flex-shrink-0">
                    <svg className="w-5 h-5 text-[#AAD816]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-zinc-900 leading-tight">{gymData.gymAddressLine}</p>
                    <p className="text-xs font-medium text-zinc-500 mt-1 uppercase tracking-tight">
                      {gymData.gymCity}{gymData.gymPincode ? `, ${gymData.gymPincode}` : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── WhatsApp OTP Verification Section ── */}
              <div className="border-t border-zinc-100 pt-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-xl bg-[#25D366]/10 flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-[#25D366]" />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-zinc-900">WhatsApp Verification</p>
                    <p className="text-[10px] font-medium text-zinc-400">Verify your phone to prevent spam</p>
                  </div>
                  {otpState === 'verified' && (
                    <div className="ml-auto flex items-center gap-1.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border border-emerald-200">
                      <ShieldCheck className="w-3 h-3" />
                      Verified
                    </div>
                  )}
                </div>

                {/* IDLE: show editable phone + verify button */}
                {otpState === 'idle' && (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="verifyPhone" className="block text-xs font-black uppercase tracking-widest text-zinc-400 ml-1 mb-2">
                        WhatsApp Phone Number *
                      </label>
                      <div className="flex items-center gap-0 border-2 border-zinc-200 rounded-2xl overflow-hidden focus-within:border-[#AAD816] transition-all">
                        <span className="px-4 py-4 text-sm font-black text-zinc-400 bg-zinc-50 border-r border-zinc-200 select-none">+91</span>
                        <input
                          id="verifyPhone"
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={verifyPhone}
                          onChange={handleVerifyPhoneChange}
                          className="flex-1 px-4 py-4 bg-white text-sm font-bold text-zinc-900 outline-none placeholder-zinc-300"
                          placeholder="98765 43210"
                        />
                      </div>
                      <p className="text-[10px] font-medium text-zinc-400 ml-1 mt-1.5">This will be saved as the gym's contact number.</p>
                    </div>
                    <button
                      id="verify-phone-btn"
                      onClick={handleVerifyPhoneClick}
                      disabled={!verifyPhone || verifyPhone.length !== 10}
                      className="w-full py-4 bg-[#25D366] text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-[#1ebe5d] transition-all shadow-lg shadow-[#25D366]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Send WhatsApp OTP
                    </button>
                  </div>
                )}

                {/* WAITING INPUT / VERIFYING: show OTP input */}
                {(otpState === 'waiting_input' || otpState === 'verifying') && (
                  <div className="space-y-5">
                    <div className="text-center">
                      <p className="text-xs font-bold text-zinc-500">OTP sent to</p>
                      <p className="text-sm font-black text-zinc-900 mt-0.5">{phoneFormatted}</p>
                    </div>

                    <OtpInput
                      value={otpValue}
                      onChange={(v) => { setOtpValue(v); setOtpError(null); }}
                      disabled={otpState === 'verifying'}
                    />

                    {otpError && (
                      <p className="text-center text-xs font-bold text-red-500">{otpError}</p>
                    )}

                    <button
                      id="submit-otp-btn"
                      onClick={handleVerifyOtp}
                      disabled={otpValue.length !== 6 || otpState === 'verifying'}
                      className="w-full py-4 bg-zinc-950 text-[#AAD816] rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-[#AAD816] hover:text-zinc-950 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {otpState === 'verifying' ? (
                        <>
                          <div className="w-4 h-4 border-2 border-[#AAD816]/30 border-t-[#AAD816] rounded-full animate-spin" />
                          Verifying…
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="w-4 h-4" />
                          Verify OTP
                        </>
                      )}
                    </button>

                    {/* Resend */}
                    <div className="text-center">
                      {resendCooldown > 0 ? (
                        <p className="text-xs text-zinc-400 font-medium">
                          Resend in <span className="font-black text-zinc-600">{resendCooldown}s</span>
                        </p>
                      ) : (
                        <button
                          onClick={handleResendOtp}
                          disabled={otpState === 'verifying'}
                          className="text-xs font-black text-zinc-400 hover:text-[#25D366] transition-colors flex items-center gap-1 mx-auto disabled:opacity-50"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* VERIFIED: success state with change option */}
                {otpState === 'verified' && (
                  <div className="space-y-3">
                    <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-black text-emerald-700">Phone Verified!</p>
                        <p className="text-xs text-emerald-600 font-medium mt-0.5">{phoneFormatted}</p>
                      </div>
                      <button
                        onClick={() => { setOtpState('idle'); setOtpValue(''); setOtpError(null); setResendCooldown(0); }}
                        className="text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-10 mt-10 border-t border-zinc-100">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1 || submitting}
              className="px-8 py-3 text-xs font-black uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-900 disabled:opacity-0 transition-all duration-300"
            >
              Back
            </button>
            {currentStep < 2 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!validateStep(currentStep) || submitting || validatingGstin}
                className="px-10 py-4 bg-zinc-950 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-[#AAD816] hover:text-zinc-950 transition-all duration-300 shadow-xl shadow-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed group flex items-center gap-3"
              >
                {validatingGstin ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    Next Step
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !validateStep(currentStep)}
                className="px-12 py-4 bg-zinc-950 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-[#AAD816] hover:text-zinc-950 transition-all duration-300 shadow-xl shadow-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 active:scale-95"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Deploying...
                  </>
                ) : (
                  'Launch Gym'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}